import { Contact } from '../types/Contact';

export interface ParsedGoogleData {
    linkedinUrl?: string;
    linkedinDescription?: string;
    tldr?: string;
    about?: string;
    comprehensiveNotes?: string;
    parsedNotes?: {
        experience?: string;
        education?: string;
        skills?: string;
        achievements?: string;
        interests?: string;
        connections?: string;
        contact?: string;
        notes?: string;
        location?: string;
        company?: string;
        highlights?: string[];
        timeline?: string[];
        keyPhrases?: string[];
        paragraphs?: string[];
    };
}

export interface StructuredNotesData {
    linkedin_profile_url?: string;
    linkedin_description?: string;
    tldr_summary?: string;
    about_section?: string;
    comprehensive_notes?: string;
    professional_experience?: string;
    education_background?: string;
    skills_expertise?: string;
    achievements?: string;
    personal_interests?: string;
    location_info?: string;
    company_details?: string;
    contact_preferences?: string;
    parsed_at: string;
}

export function parseGoogleContactData(biographyText: string | null): ParsedGoogleData | null {
    if (!biographyText) return null;

    const data: ParsedGoogleData = {};

    // Extract LinkedIn URL
    const linkedinMatch = biographyText.match(/LinkedIn URL:\s*(https?:\/\/[^\s\n]+)/);
    if (linkedinMatch) data.linkedinUrl = linkedinMatch[1];

    // Extract LinkedIn description (first line after LinkedIn URL)
    const linkedinDescMatch = biographyText.match(/LinkedIn:\s*([^\n]+)/);
    if (linkedinDescMatch) data.linkedinDescription = linkedinDescMatch[1];

    // Extract TLDR section
    const tldrMatch = biographyText.match(/TLDR:\s*([^]+?)(?=\nAbout:|$)/);
    if (tldrMatch) data.tldr = tldrMatch[1].trim();

    // Extract About section
    const aboutMatch = biographyText.match(/About:\s*([^]+?)(?=\nComprehensive Notes:|$)/);
    if (aboutMatch) data.about = aboutMatch[1].trim();

    // Extract Comprehensive Notes
    const notesMatch = biographyText.match(/Comprehensive Notes:\s*([^]+?)(?=\n\[LinkedIn Profile|$)/);
    if (notesMatch) {
        const rawNotes = notesMatch[1].trim();
        data.comprehensiveNotes = rawNotes;
        data.parsedNotes = parseComprehensiveNotes(rawNotes);
    }

    return data;
}

function parseComprehensiveNotes(notesText: string) {
    const sections: any = {};

    // Common patterns to look for in comprehensive notes
    const patterns = {
        experience: /(?:Experience|Work Experience|Career|Background):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        education: /(?:Education|School|University|College|Degree):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        skills: /(?:Skills|Expertise|Competencies|Specialties):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        achievements: /(?:Achievements|Awards|Recognition|Accomplishments):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        interests: /(?:Interests|Hobbies|Personal|Passionate about):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        connections: /(?:Connections|Mutual|Network|Referrals?):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        contact: /(?:Contact|Reach|Available|Phone|Email):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        notes: /(?:Notes|Additional|Misc|Other):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        location: /(?:Location|Based|Located|Area):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i,
        company: /(?:Company|Organization|Firm|Business):\s*([^]+?)(?=\n(?:[A-Z][a-z]+:|\n|$))/i
    };

    // Extract sections based on patterns
    Object.entries(patterns).forEach(([key, pattern]) => {
        const match = notesText.match(pattern);
        if (match && match[1].trim()) {
            sections[key] = match[1].trim();
        }
    });

    // Look for bullet points or numbered lists
    const bulletPoints = notesText.match(/^[•\-\*]\s+(.+)$/gm);
    if (bulletPoints) {
        sections.highlights = bulletPoints.map(point => point.replace(/^[•\-\*]\s+/, ''));
    }

    // Look for years/dates (potential timeline items)
    const timelineItems = notesText.match(/(?:19|20)\d{2}[^\n]+/g);
    if (timelineItems) {
        sections.timeline = timelineItems;
    }

    // Extract key phrases (things in quotes or parentheses)
    const keyPhrases = notesText.match(/"([^"]+)"|'([^']+)'|\(([^)]+)\)/g);
    if (keyPhrases) {
        sections.keyPhrases = keyPhrases.map(phrase => phrase.replace(/["'()]/g, ''));
    }

    // If no structured sections found, split by paragraphs for better readability
    if (Object.keys(sections).length === 0) {
        const paragraphs = notesText.split('\n\n').filter(p => p.trim().length > 0);
        if (paragraphs.length > 1) {
            sections.paragraphs = paragraphs;
        }
    }

    return sections;
}

/**
 * Parse notes into structured database fields
 * This function extracts structured data from raw notes and returns it in a format
 * that can be directly saved to the database
 */
export function parseNotesToStructuredFields(notesText: string | null): StructuredNotesData {
    if (!notesText) {
        return { parsed_at: new Date().toISOString() };
    }

    const data: StructuredNotesData = {
        parsed_at: new Date().toISOString()
    };

    // Extract LinkedIn URL
    const linkedinMatch = notesText.match(/LinkedIn URL:\s*(https?:\/\/[^\s\n]+)/);
    if (linkedinMatch) data.linkedin_profile_url = linkedinMatch[1];

    // Extract LinkedIn description (first line after LinkedIn URL)
    const linkedinDescMatch = notesText.match(/LinkedIn:\s*([^\n]+)/);
    if (linkedinDescMatch) data.linkedin_description = linkedinDescMatch[1];

    // Extract TLDR section
    const tldrMatch = notesText.match(/TLDR:\s*([^]+?)(?=\n(?:About:|Comprehensive Notes:|LinkedIn Profile|\[|\n\n|$))/);
    if (tldrMatch) data.tldr_summary = tldrMatch[1].trim();

    // Extract About section
    const aboutMatch = notesText.match(/About:\s*([^]+?)(?=\n(?:Comprehensive Notes:|LinkedIn Profile|\[|\n\n|$))/);
    if (aboutMatch) data.about_section = aboutMatch[1].trim();

    // Extract Comprehensive Notes
    const notesMatch = notesText.match(/Comprehensive Notes:\s*([^]+?)(?=\n(?:LinkedIn Profile|\[|\n\n|$))/);
    if (notesMatch) {
        const rawNotes = notesMatch[1].trim();
        data.comprehensive_notes = rawNotes;

        // Parse the comprehensive notes into specific fields
        const parsedSections = parseComprehensiveNotes(rawNotes);

        if (parsedSections.experience) data.professional_experience = parsedSections.experience;
        if (parsedSections.education) data.education_background = parsedSections.education;
        if (parsedSections.skills) data.skills_expertise = parsedSections.skills;
        if (parsedSections.achievements) data.achievements = parsedSections.achievements;
        if (parsedSections.interests) data.personal_interests = parsedSections.interests;
        if (parsedSections.location) data.location_info = parsedSections.location;
        if (parsedSections.company) data.company_details = parsedSections.company;
        if (parsedSections.contact) data.contact_preferences = parsedSections.contact;
    }

    // If no structured sections found but there's content, try to extract from the whole text
    if (!data.comprehensive_notes && notesText.length > 100) {
        // Look for common patterns in the entire notes text
        const experienceMatch = notesText.match(/(?:Experience|Work|Career|Position|Job|Role)[\s:]*([^.\n]+(?:\.[^.\n]+)*)/i);
        if (experienceMatch) data.professional_experience = experienceMatch[1].trim();

        const educationMatch = notesText.match(/(?:Education|School|University|College|Degree|Study|Studies)[\s:]*([^.\n]+(?:\.[^.\n]+)*)/i);
        if (educationMatch) data.education_background = educationMatch[1].trim();

        const skillsMatch = notesText.match(/(?:Skills|Expertise|Specializ|Expert in|Good at)[\s:]*([^.\n]+(?:\.[^.\n]+)*)/i);
        if (skillsMatch) data.skills_expertise = skillsMatch[1].trim();

        const companyMatch = notesText.match(/(?:Company|Organization|Firm|Business|Works at|Employed by)[\s:]*([^.\n]+)/i);
        if (companyMatch) data.company_details = companyMatch[1].trim();
    }

    return data;
}

/**
 * Check if a contact needs parsing based on notes content and parsed_at timestamp
 */
export function shouldParseContact(contact: Contact): boolean {
    // If there are no notes, nothing to parse
    if (!contact.notes || contact.notes.trim().length === 0) {
        return false;
    }

    // If never parsed before, definitely parse
    if (!contact.parsed_at) {
        return true;
    }

    // If notes were updated after last parsing, re-parse
    if (contact.updated_at && contact.parsed_at) {
        const updatedAt = new Date(contact.updated_at);
        const parsedAt = new Date(contact.parsed_at);
        return updatedAt > parsedAt;
    }

    return false;
}

export function getTierColor(tier: string): string {
    switch (tier) {
        case 'A': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        case 'B': return 'bg-blue-100 text-blue-800 border-blue-300';
        case 'C': return 'bg-green-100 text-green-800 border-green-300';
        case 'D': return 'bg-gray-100 text-gray-800 border-gray-300';
        default: return 'bg-gray-100 text-gray-600 border-gray-300';
    }
}

export function getStatusColor(status: string): string {
    switch (status) {
        case 'Called': return 'bg-green-100 text-green-800';
        case 'Follow-up': return 'bg-orange-100 text-orange-800';
        case 'Meeting Scheduled': return 'bg-blue-100 text-blue-800';
        case 'Closed': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-600';
    }
}

export function formatDate(dateString: string | null): string {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
}
